import { FC, useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { FocusClassOption } from '../../custom-hooks/useFocusClassData';
import { FocusReachSummary } from './FocusReachSummary';

interface Props {
  currentClassId: string;
  currentClassLabel: string;
  options: FocusClassOption[];
  loading: boolean;
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  onBack: () => void;
  onDone: () => void;
  reached: number;
  total: number;
  finished: number;
}

const LINK =
  'tw-bg-transparent tw-border-none tw-py-1 tw-text-indigo-600 tw-font-atkinson tw-font-bold tw-text-sm tw-leading-5 tw-cursor-pointer tw-inline-flex tw-items-center tw-gap-1';

const ROW = 'tw-flex tw-items-center tw-gap-2 tw-w-full';
const ROW_LABEL = 'tw-font-atkinson tw-text-sm tw-leading-5';

const CHECKBOX_BASE =
  'tw-size-[22px] tw-rounded-md tw-shrink-0 tw-inline-flex tw-items-center tw-justify-center tw-box-border tw-p-0';

const Checkbox: FC<{ checked: boolean; disabled?: boolean; onClick?: () => void; ariaLabel: string }> = ({
  checked,
  disabled = false,
  onClick,
  ariaLabel,
}) => {
  const variant = disabled
    ? 'tw-bg-neutral-600 tw-border-none tw-cursor-default'
    : checked
    ? 'tw-bg-indigo-600 tw-border-none tw-cursor-pointer'
    : 'tw-bg-white tw-border-2 tw-border-neutral-950 tw-cursor-pointer';
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      className={`${CHECKBOX_BASE} ${variant}`}
    >
      {checked && <Check className="tw-size-3.5 tw-text-white" strokeWidth={3} aria-hidden />}
    </button>
  );
};

export const ClassSelectStep: FC<Props> = ({
  currentClassId,
  currentClassLabel,
  options,
  loading,
  selectedIds,
  onChange,
  onBack,
  onDone,
  reached,
  total,
  finished,
}) => {
  const [search, setSearch] = useState('');

  const labelFor = (o: FocusClassOption) => (o.id === currentClassId ? currentClassLabel : o.label);
  const query = search.trim().toLowerCase();
  const visible = options.filter(o => labelFor(o).toLowerCase().includes(query));

  const currentIsOption = !!currentClassId && options.some(o => o.id === currentClassId);
  const isChecked = (id: string) => selectedIds.includes(id) || (currentIsOption && id === currentClassId);
  const emit = (ids: string[]) =>
    onChange(currentIsOption && !ids.includes(currentClassId) ? [...ids, currentClassId] : ids);

  const visibleIds = visible.map(o => o.id);
  const allVisibleSelected = visible.length > 0 && visible.every(o => isChecked(o.id));
  const toggleAll = () =>
    emit(
      allVisibleSelected
        ? selectedIds.filter(id => !visibleIds.includes(id))
        : [...new Set([...selectedIds, ...visibleIds])],
    );

  const toggleOne = (id: string) =>
    emit(selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id]);

  return (
    <div className="tw-flex tw-flex-col">
      <div className="tw-border-solid tw-border-b-2 tw-border-neutral-100 tw-pt-4 tw-px-4 tw-pb-2 tw-flex tw-items-center tw-gap-3">
        <button type="button" onClick={onBack} className={LINK}>
          <ArrowLeft className="tw-size-4" />
          Back
        </button>
        <span className="tw-font-sora tw-font-bold tw-text-sm tw-leading-5 tw-text-neutral-950">Select classes</span>
      </div>

      <div className="tw-flex tw-flex-col tw-gap-4 tw-p-4 tw-max-h-[50vh] tw-overflow-y-auto">
        <div className="tw-flex tw-items-center tw-gap-2 tw-bg-white tw-border-solid tw-border-2 tw-border-neutral-200 tw-rounded-full tw-py-2 tw-px-4">
          <i className="pi pi-search tw-text-[18px] tw-text-neutral-950" aria-hidden />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by class name"
            aria-label="Search by class name"
            className="nc_focus-class-search tw-flex-1 tw-min-w-0 tw-border-none tw-outline-none tw-bg-transparent tw-font-atkinson tw-text-sm tw-leading-5 tw-text-neutral-950"
          />
        </div>

        <div className={ROW}>
          <Checkbox checked={allVisibleSelected} onClick={toggleAll} ariaLabel="Select all eligible classes" />
          <span className={`${ROW_LABEL} tw-text-neutral-950`}>Select all eligible</span>
        </div>

        {loading ? (
          <p className={`${ROW_LABEL} tw-text-neutral-700`}>Loading classes…</p>
        ) : (
          visible.map(o => {
            const isCurrent = o.id === currentClassId;
            return (
              <div key={o.id} className={ROW}>
                <Checkbox
                  checked={isChecked(o.id)}
                  disabled={isCurrent}
                  onClick={isCurrent ? undefined : () => toggleOne(o.id)}
                  ariaLabel={`${isCurrent ? 'Current class, locked: ' : ''}${labelFor(o)}`}
                />
                <span className={`${ROW_LABEL} ${isCurrent ? 'tw-text-neutral-600' : 'tw-text-neutral-950'}`}>
                  {labelFor(o)}
                </span>
                {isCurrent && (
                  <span className="tw-inline-flex tw-items-center tw-h-[18px] tw-py-0.5 tw-px-2 tw-rounded-md tw-bg-indigo-50 tw-border tw-border-[#c2c9fe] tw-font-atkinson tw-font-bold tw-text-xs tw-leading-4 tw-text-neutral-950">
                    Current class
                  </span>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className="tw-flex tw-flex-col tw-gap-2 tw-border-solid tw-border-t-2 tw-border-neutral-100 tw-px-4 tw-py-3">
        <div className="tw-rounded-xl tw-border-solid tw-border-2 tw-border-neutral-100 tw-bg-neutral-50 tw-px-3 tw-py-2 tw-font-atkinson tw-text-xs tw-leading-4 tw-text-neutral-950">
          <FocusReachSummary reached={reached} total={total} finished={finished} />
        </div>
        <button
          type="button"
          onClick={onDone}
          className="tw-bg-indigo-600 tw-border-none tw-rounded-full tw-py-3 tw-px-6 tw-w-full tw-font-atkinson tw-font-bold tw-text-base tw-leading-5 tw-text-white tw-cursor-pointer"
        >
          Done
        </button>
      </div>
    </div>
  );
};
